import { useState } from 'preact/hooks';
import { Crosshair, Dices, FileText, Gamepad2, GraduationCap, Hand, Heart, LogIn, LogOut, Maximize, Monitor, ScrollText, Settings as SettingsIcon, ShieldCheck, Trash, UserRound, Users, Volume2, VolumeX } from 'lucide-preact';
import type { ComponentChildren } from 'preact';
import { useEffect } from 'preact/hooks';
import { actionCodes, BIND_ACTIONS, resolveBindings, type BindAction } from '../../game/input/Input';
import { audio } from '../../game/audio/Audio';
import { canFullscreen, enterFullscreen } from '../fullscreen';
import { copyText } from '../platform';
import { autoQuality, settings, updateSettings, type ClientSettings } from '../settings';
import { rename } from '../state/actions';
import { nameProblem } from '../state/names';
import { useNameRoller } from './Onboarding';
import { RENAME_PRICE } from '@vm/content';
import { go, replace, route } from '../state/nav';
import { startTutorial } from '../state/play';
import { deleteAccount, login, logout, registerEmail } from '../state/session';
import { mutedName, muteUser } from '../state/socialActions';
import { isOffline, me, mutedUsers, restricted, toast } from '../state/store';
import { AsyncButton, confirmDialog, Field, OnlineOnly, Price, Screen, Segmented, Slider, Toggle } from '../ui/kit';
import { ForgotPasswordLink } from './PasswordReset';

type Section = 'controls' | 'graphics' | 'audio' | 'crosshair' | 'touch' | 'social' | 'account' | 'about';

const SECTIONS: { id: Section; label: string; icon: ComponentChildren }[] = [
  { id: 'controls', label: 'Controls', icon: <Gamepad2 size={20} /> },
  { id: 'graphics', label: 'Graphics', icon: <Monitor size={20} /> },
  { id: 'audio', label: 'Audio', icon: <Volume2 size={20} /> },
  { id: 'crosshair', label: 'Crosshair', icon: <Crosshair size={20} /> },
  { id: 'touch', label: 'Touch', icon: <Hand size={20} /> },
  { id: 'social', label: 'Social', icon: <Users size={20} /> },
  { id: 'account', label: 'Account', icon: <UserRound size={20} /> },
  { id: 'about', label: 'About', icon: <FileText size={20} /> },
];

const set = (patch: Partial<ClientSettings>): void => updateSettings(patch);
const pct = (v: number): string => `${Math.round(v * 100)}%`;

function keyLabel(code: string): string {
  if (code.startsWith('Key')) return code.slice(3);
  if (code.startsWith('Digit')) return code.slice(5);
  const map: Record<string, string> = { ArrowUp: '↑', ArrowDown: '↓', ArrowLeft: '←', ArrowRight: '→', Space: 'Space', Tab: 'Tab', Enter: 'Enter', Escape: 'Esc' };
  return map[code] ?? code;
}

function Keys(props: { codes: string[] }) {
  return (
    <span class="keys">
      {props.codes.map((c) => (
        <kbd key={c}>{keyLabel(c)}</kbd>
      ))}
    </span>
  );
}

function Controls() {
  const s = settings.value;
  const b = resolveBindings(s.keybinds);
  const [capturing, setCapturing] = useState<BindAction | null>(null);
  useEffect(() => {
    if (!capturing) return;
    const onKey = (e: KeyboardEvent): void => {
      e.preventDefault();
      e.stopPropagation();
      if (e.code !== 'Escape') set({ keybinds: { ...s.keybinds, [capturing]: e.code } });
      setCapturing(null);
    };
    window.addEventListener('keydown', onKey, { capture: true });
    return () => window.removeEventListener('keydown', onKey, { capture: true });
  }, [capturing]);
  const custom = Object.keys(s.keybinds ?? {}).length > 0;
  return (
    <>
      <div class="set-group">
        <Slider label="Look sensitivity" value={s.sensitivity} min={0.1} max={4} step={0.05} format={(v) => v.toFixed(2)} onChange={(v) => set({ sensitivity: v })} />
        <Slider label="Field of view" value={s.fov} min={60} max={105} step={1} format={(v) => `${v}°`} onChange={(v) => set({ fov: v })} />
        <Toggle label="Invert look" hint="Moving the mouse up looks down." checked={s.invertY} onChange={(v) => set({ invertY: v })} />
      </div>
      <h3 class="set-title">Keyboard & mouse</h3>
      <p class="set-hint">Click a key to change it, then press the new key (Esc cancels).</p>
      <div class="keymap">
        {BIND_ACTIONS.map(({ action, label }) => (
          <button key={action} type="button" class={`keymap-row rebind ${capturing === action ? 'capturing' : ''}`} onClick={() => setCapturing(capturing === action ? null : action)}>
            <span>{label}</span>
            {capturing === action ? (
              <span class="keys">
                <kbd>Press a key…</kbd>
              </span>
            ) : (
              <Keys codes={actionCodes(b, action).length ? actionCodes(b, action) : ['—']} />
            )}
          </button>
        ))}
        <div class="keymap-row">
          <span>Shoot / aim</span>
          <span class="keys">
            <kbd>Left click</kbd>
            <kbd>Right click</kbd>
          </span>
        </div>
        <div class="keymap-row">
          <span>Menu</span>
          <Keys codes={['Escape']} />
        </div>
      </div>
      {custom && (
        <button type="button" class="btn small" onClick={() => set({ keybinds: {} })}>
          Reset keys to defaults
        </button>
      )}
    </>
  );
}

function Graphics() {
  const s = settings.value;
  return (
    <div class="set-group">
      <Field label="Graphics quality" hint={s.quality === 'auto' ? `Auto picks ${autoQuality()} on this device.` : 'Lower settings run smoother on older devices.'}>
        <Segmented
          options={[
            { value: 'auto', label: 'Auto' },
            { value: 'low', label: 'Low' },
            { value: 'medium', label: 'Medium' },
            { value: 'high', label: 'High' },
          ]}
          value={s.quality}
          onChange={(v) => set({ quality: v })}
        />
      </Field>
      <Toggle label="Show FPS counter" checked={s.showFps} onChange={(v) => set({ showFps: v })} />
      <Toggle label="Reduce menu motion" hint="Fewer animations in the menus." checked={s.reduceMotion} onChange={(v) => set({ reduceMotion: v })} />
      {canFullscreen() && (
        <>
          <Toggle label="Full screen when playing" hint="Hides the browser's toolbars and turns the game sideways when a match starts." checked={s.autoFullscreen} onChange={(v) => set({ autoFullscreen: v })} />
          <button class="btn" onClick={() => void enterFullscreen()}>
            <Maximize size={18} /> Full screen now
          </button>
        </>
      )}
    </div>
  );
}

function AudioSection() {
  const s = settings.value;
  return (
    <div class="set-group">
      <Slider label="Master volume" value={s.masterVolume} min={0} max={1} step={0.05} format={pct} onChange={(v) => set({ masterVolume: v })} />
      <Slider label="Effects" value={s.sfxVolume} min={0} max={1} step={0.05} format={pct} onChange={(v) => set({ sfxVolume: v })} />
      <Slider label="Music" value={s.musicVolume} min={0} max={1} step={0.05} format={pct} onChange={(v) => set({ musicVolume: v })} />
      <button
        class="btn"
        onClick={() => {
          audio.unlock();
          audio.play('ui_buy');
        }}
      >
        <Volume2 size={18} /> Test sound
      </button>
    </div>
  );
}

const XH_COLORS = ['#ffffff', '#ffd24a', '#52d86a', '#4af0d0', '#4a9aff', '#ff6ab0', '#ff5a4a'];

function CrosshairSection() {
  const s = settings.value;
  return (
    <div class="set-group">
      <div class="xh-preview">
        <div class="crosshair" style={{ '--gap': '9px', '--xh': s.crosshairColor } as Record<string, string>}>
          {s.crosshairStyle === 'cross' && (
            <>
              <i class="xh xh-t" />
              <i class="xh xh-b" />
              <i class="xh xh-l" />
              <i class="xh xh-r" />
            </>
          )}
          {s.crosshairStyle === 'circle' && <i class="xh-circle" />}
          <i class="xh-dot" />
        </div>
      </div>
      <Field label="Style">
        <Segmented
          options={[
            { value: 'cross', label: 'Cross' },
            { value: 'dot', label: 'Dot' },
            { value: 'circle', label: 'Circle' },
          ]}
          value={s.crosshairStyle}
          onChange={(v) => set({ crosshairStyle: v })}
        />
      </Field>
      <Field label="Color">
        <div class="swatches">
          {XH_COLORS.map((c) => (
            <button key={c} class={`swatch ${s.crosshairColor === c ? 'is-on' : ''}`} style={{ background: c }} aria-label={`Crosshair color ${c}`} onClick={() => set({ crosshairColor: c })} />
          ))}
        </div>
      </Field>
    </div>
  );
}

function Touch() {
  const s = settings.value;
  return (
    <div class="set-group">
      <Field label="On-screen controls">
        <Segmented
          options={[
            { value: 'auto', label: 'Auto' },
            { value: 'on', label: 'Always' },
            { value: 'off', label: 'Off' },
          ]}
          value={s.touchControls}
          onChange={(v) => set({ touchControls: v })}
        />
      </Field>
      <Slider label="Button size" value={s.touchScale} min={0.7} max={1.4} step={0.05} format={pct} onChange={(v) => set({ touchScale: v })} />
      <Toggle label="Auto-fire" hint="Shoot automatically when your crosshair is on an enemy." checked={s.autoFire} onChange={(v) => set({ autoFire: v })} />
      <Toggle label="Aim assist" hint="Slightly slows aiming over enemies on touch screens and controllers." checked={s.aimAssist} onChange={(v) => set({ aimAssist: v })} />
    </div>
  );
}

function Social() {
  const s = settings.value;
  const muted = [...mutedUsers.value];
  return (
    <>
      <div class="set-group">
        <Toggle label="Show chat" hint="Turn off to hide the lobby chat completely." checked={s.chatEnabled} onChange={(v) => set({ chatEnabled: v })} />
      </div>
      <h3 class="set-title">Muted players</h3>
      {muted.length ? (
        <div class="set-list">
          {muted.map((id) => (
            <div key={id} class="set-list-row">
              <VolumeX size={18} />
              <span>{mutedName(id)}</span>
              <button class="btn sm push-right" onClick={() => muteUser(id, false)}>
                Unmute
              </button>
            </div>
          ))}
        </div>
      ) : (
        <p class="muted small">Players you mute from chat appear here. Mutes only affect you.</p>
      )}
      <button class="btn" onClick={() => go('friends', { tab: 'blocked' })}>
        Blocked players
      </button>
    </>
  );
}

function NameChanger() {
  const m = me.value!;
  const kid = m.restricted;
  const [name, setName] = useState(m.name);
  const roll = useNameRoller();
  const problem = nameProblem(name);
  return (
    <div class="set-group">
      <Field
        label="Call sign"
        hint={m.renamed ? `Name changes cost ${RENAME_PRICE.price} ${RENAME_PRICE.currency}.` : 'Your first name change is free.'}
        error={name !== m.name ? problem : null}
      >
        <div class="ui-input-row">
          {kid ? (
            <div class="nameplate sm">{name}</div>
          ) : (
            <input class="ui-input" value={name} maxLength={16} onInput={(e) => setName((e.currentTarget as HTMLInputElement).value.replace(/\s/g, ''))} />
          )}
          <AsyncButton class="icon" aria-label="Suggest a name" onClick={async () => setName(await roll())}>
            <Dices size={20} />
          </AsyncButton>
          <AsyncButton
            class="primary"
            disabled={name === m.name || !!problem || isOffline.value}
            onClick={async () => {
              if (
                m.renamed &&
                !(await confirmDialog({
                  title: 'Change your name?',
                  body: (
                    <span>
                      This name change costs <Price price={RENAME_PRICE.price} currency={RENAME_PRICE.currency} />
                    </span>
                  ),
                  confirm: 'Change',
                }))
              )
                return;
              if (await rename(name)) toast(`You're now ${name}!`, { kind: 'success' });
            }}
          >
            Save
          </AsyncButton>
        </div>
      </Field>
    </div>
  );
}

function LinkEmail() {
  const [email, setEmail] = useState('');
  const [pw, setPw] = useState('');
  const valid = /.+@.+\..+/.test(email) && pw.length >= 8;
  return (
    <form class="set-group" onSubmit={(e) => e.preventDefault()}>
      <p class="muted small">Link an email to keep your marine safe and play on other devices.</p>
      <div class="form-grid">
        <Field label="Email">
          <input class="ui-input" type="email" autoComplete="email" value={email} onInput={(e) => setEmail((e.currentTarget as HTMLInputElement).value)} />
        </Field>
        <Field label="Password" hint="At least 8 characters.">
          <input class="ui-input" type="password" autoComplete="new-password" value={pw} onInput={(e) => setPw((e.currentTarget as HTMLInputElement).value)} />
        </Field>
      </div>
      <AsyncButton class="green" disabled={!valid} onClick={() => registerEmail(email, pw)}>
        <ShieldCheck size={18} /> Link email
      </AsyncButton>
    </form>
  );
}

function LoginOther() {
  const [email, setEmail] = useState('');
  const [pw, setPw] = useState('');
  const m = me.value!;
  const valid = /.+@.+\..+/.test(email) && pw.length >= 6;
  return (
    <form class="set-group" onSubmit={(e) => e.preventDefault()}>
      <p class="muted small">Already have a linked account from another device? Log in here.</p>
      <div class="form-grid">
        <Field label="Email">
          <input class="ui-input" type="email" autoComplete="email" value={email} onInput={(e) => setEmail((e.currentTarget as HTMLInputElement).value)} />
        </Field>
        <Field label="Password">
          <input class="ui-input" type="password" autoComplete="current-password" value={pw} onInput={(e) => setPw((e.currentTarget as HTMLInputElement).value)} />
        </Field>
      </div>
      <AsyncButton
        disabled={!valid}
        onClick={async () => {
          if (m.isGuest && !(await confirmDialog({ title: 'Switch accounts?', body: "This guest marine isn't linked to an email. If you switch, you can't come back to it.", confirm: 'Switch', danger: true })))
            return;
          await login(email, pw);
        }}
      >
        <LogIn size={18} /> Log in
      </AsyncButton>
      <ForgotPasswordLink email={email} />
    </form>
  );
}

function DeleteAccount() {
  const [typed, setTyped] = useState('');
  return (
    <div class="danger-zone">
      <h4>Delete account</h4>
      <p class="muted small">Permanently deletes your marine, progress, items and friends. Purchases can't be refunded after deletion.</p>
      <div class="ui-input-row">
        <input class="ui-input" placeholder='Type "DELETE" to confirm' value={typed} onInput={(e) => setTyped((e.currentTarget as HTMLInputElement).value)} />
        <AsyncButton
          class="danger"
          disabled={typed !== 'DELETE'}
          onClick={async () => {
            if (await confirmDialog({ title: 'Delete forever?', body: 'This cannot be undone.', confirm: 'Delete my account', danger: true })) await deleteAccount();
          }}
        >
          <Trash size={18} /> Delete
        </AsyncButton>
      </div>
    </div>
  );
}

function Account() {
  const m = me.value;
  if (!m) return null;
  return (
    <OnlineOnly feature="Account settings">
      <div class="account-card panel">
        <UserRound size={34} />
        <div>
          <b>{m.name}</b>
          <span class="muted small">{m.email ? `Linked to ${m.email}` : m.restricted ? 'Guest account (restricted mode)' : 'Guest account — not backed up'}</span>
        </div>
        {m.code && (
          <button
            class="friend-code sm"
            onClick={async () => {
              if (await copyText(m.code)) toast('Friend code copied', { kind: 'success' });
            }}
          >
            <span>Code</span>
            <b>{m.code}</b>
          </button>
        )}
      </div>
      <NameChanger />
      {!m.email &&
        (restricted.value ? (
          <p class="muted small">Backing up with an email isn't available on your account. Ask a parent if you need help.</p>
        ) : (
          <>
            <h3 class="set-title">Back up your account</h3>
            <LinkEmail />
          </>
        ))}
      <h3 class="set-title">Log in on this device</h3>
      <LoginOther />
      <h3 class="set-title">Session</h3>
      <div class="set-group">
        <AsyncButton
          onClick={async () => {
            const warn = m.isGuest && !m.email;
            if (
              await confirmDialog({
                title: 'Log out?',
                body: warn ? "You're playing as a guest. If you log out without linking an email, you could lose this marine." : 'You can log back in with your email any time.',
                confirm: 'Log out',
                danger: warn,
              })
            )
              await logout();
          }}
        >
          <LogOut size={18} /> Log out
        </AsyncButton>
      </div>
      <DeleteAccount />
    </OnlineOnly>
  );
}

function About() {
  return (
    <>
      <div class="about-links">
        <button class="btn" onClick={() => go('legal', { doc: 'privacy' })}>
          <ShieldCheck size={18} /> Privacy Policy
        </button>
        <button class="btn" onClick={() => go('legal', { doc: 'terms' })}>
          <ScrollText size={18} /> Terms of Service
        </button>
        <button class="btn" onClick={() => go('legal', { doc: 'parents' })}>
          <Users size={18} /> Parents' Guide
        </button>
        <button class="btn" onClick={() => go('legal', { doc: 'credits' })}>
          <Heart size={18} /> Credits
        </button>
      </div>
      <div class="set-group">
        <button class="btn green" onClick={() => startTutorial()}>
          <GraduationCap size={18} /> Replay Boot Camp
        </button>
      </div>
      <p class="muted small">Voxel Marines v{__APP_VERSION__}</p>
    </>
  );
}

export function SettingsScreen() {
  const section = (route.value.params.tab as Section) || 'controls';
  return (
    <Screen title="Settings" icon={<SettingsIcon size={26} />}>
      <div class="settings">
        <nav class="settings-nav">
          {SECTIONS.map((s) => (
            <button
              key={s.id}
              class={`settings-nav-btn ${s.id === section ? 'is-on' : ''}`}
              onClick={() => {
                audio.play('ui_click');
                replace('settings', { tab: s.id });
              }}
            >
              {s.icon}
              <span>{s.label}</span>
            </button>
          ))}
        </nav>
        <div class="settings-body scr-scroll" key={section}>
          <h2 class="settings-title">{SECTIONS.find((s) => s.id === section)?.label}</h2>
          {section === 'controls' && <Controls />}
          {section === 'graphics' && <Graphics />}
          {section === 'audio' && <AudioSection />}
          {section === 'crosshair' && <CrosshairSection />}
          {section === 'touch' && <Touch />}
          {section === 'social' && <Social />}
          {section === 'account' && <Account />}
          {section === 'about' && <About />}
        </div>
      </div>
    </Screen>
  );
}
